import { useState, useEffect, useRef, useCallback } from 'react';
import { getToggleStatus, setToggleState } from '../services/api/provisioningApi';

/**
 * Custom hook for managing the customer toggle (ipsnet enable/disable).
 * Fetches toggle status on mount and provides toggle action with error handling.
 *
 * @param {string} customerId - The customer ID to manage toggle for
 * @returns {object} Toggle state and actions
 */
export const useCustomerToggle = (customerId) => {
  const [status, setStatus] = useState(null); // ToggleStatusResult
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  /**
   * Fetch current toggle status
   */
  const fetchStatus = useCallback(async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getToggleStatus(customerId);
      setStatus(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch toggle status');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  /**
   * Toggle the customer state (enable or disable)
   * @param {boolean} enabled - Target state
   */
  const toggle = useCallback(async (enabled) => {
    if (!customerId) return;

    try {
      setToggling(true);
      setError(null);
      const response = await setToggleState(customerId, enabled);
      const result = response.data;

      if (result.success) {
        // Refresh status after successful toggle
        await fetchStatus();
      } else {
        setError(result.error || 'Toggle operation failed');
      }

      return result;
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || 'Failed to toggle customer state';
      setError(message);
      throw new Error(message);
    } finally {
      setToggling(false);
    }
  }, [customerId, fetchStatus]);

  /**
   * Convenience methods
   */
  const enable = useCallback(() => toggle(true), [toggle]);
  const disable = useCallback(() => toggle(false), [toggle]);

  /**
   * Derived state — handles both numeric (0,1,2,3) and string enum values from backend
   */
  const normalizeState = (s) => {
    if (typeof s === 'number') return ['Enabled', 'Disabled', 'NotProvisioned', 'ExternallyProvisioned', 'Unknown'][s] || 'Unknown';
    return s;
  };
  const normalizedState = status ? normalizeState(status.state) : null;
  const isEnabled = normalizedState === 'Enabled';
  const isDisabled = normalizedState === 'Disabled';
  const isNotProvisioned = normalizedState === 'NotProvisioned';
  const isExternallyProvisioned = normalizedState === 'ExternallyProvisioned';
  const isUnknown = normalizedState === 'Unknown';

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    if (!hasFetched.current && customerId) {
      hasFetched.current = true;
      fetchStatus();
    }
  }, [customerId, fetchStatus]);

  // Reset when customerId changes
  useEffect(() => {
    hasFetched.current = false;
  }, [customerId]);

  return {
    status,
    loading,
    toggling,
    error,

    // State checks
    isEnabled,
    isDisabled,
    isNotProvisioned,
    isExternallyProvisioned,
    isUnknown,

    // Actions
    toggle,
    enable,
    disable,
    fetchStatus,
    clearError: () => setError(null)
  };
};
